import {
  ApiError,
  type CatalogueItemResource,
  type MachineBillLine,
  type MachineBillResource,
  MachineBillsController_findOne_v1,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { IconArrowLeft, IconReceiptOff } from '@tabler/icons-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { isNotFound } from '@/api/failure';
import { formatInstant } from '@/lib/instant';
import { formatPaise } from '@/lib/money';

import { LoadFailed, Screen, ScreenFact } from '../back-office/Screen';
import { machineBillsForImportPath, paths } from '../paths';
import { useBasketItems, useNamedOutlet } from '../sales/sale-names';
import { formatBusinessDate } from '../tickets/business-date';
import { ReverseMachineBillDialog } from './ReverseMachineBillDialog';

/**
 * One imported bill: `GET /v1/machine-bills/{saleId}`, and its reversal.
 *
 * The page a person lands on to answer "is this bill really a sale" — lines,
 * the machine that printed it, the file rows it came from, and the reversal if
 * there is one. Laid out like the till sale's record so the two read alike.
 *
 * A till sale's id answers `404` here, and so does a bill outside reach; both
 * get the same "no bill at this address".
 */

type RecordState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly value: MachineBillResource }
  | { readonly kind: 'failed'; readonly error: unknown };

export function MachineBill() {
  const { id } = useParams();
  const client = useApiClient();

  const [state, setState] = useState<RecordState>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const [reversing, setReversing] = useState(false);

  useEffect(() => {
    if (id === undefined) return;

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(MachineBillsController_findOne_v1, {
        path: { saleId: id },
        signal: controller.signal,
      })
      .then((bill) => {
        if (live) setState({ kind: 'loaded', value: bill });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, id, reloads]);

  const bill = state.kind === 'loaded' ? state.value : null;

  const outlet = useNamedOutlet(bill?.outletId);
  const items = useBasketItems(bill?.lines);

  if (id === undefined) return <Navigate to={paths.machineBills} replace />;

  // Not found, out of reach, a till sale's id, or not a UUID at all — one answer.
  const missing =
    state.kind === 'failed' &&
    (isNotFound(state.error) ||
      (state.error instanceof ApiError && state.error.code === 'VALIDATION_FAILED'));

  const reversed = bill?.reversal != null;

  return (
    <Screen
      title="Imported bill"
      description="One bill from a billing machine, as it was imported — the lines, the machine, and the rows of the file it came from. A reversed bill is shown here and marked, never removed."
      aside={
        <>
          {bill !== null && (
            <>
              <ScreenFact label="Receipt">
                <span className="font-mono numeric">{bill.receiptNumber}</span>
                {reversed && (
                  <>
                    {' '}
                    <Badge variant="outline">Reversed</Badge>
                  </>
                )}
              </ScreenFact>

              <ScreenFact label="Total">
                <span className={`font-mono numeric ${reversed ? 'line-through' : ''}`}>
                  {formatPaise(bill.totalPaise)}
                </span>
              </ScreenFact>
            </>
          )}

          <Link
            to={paths.machineBills}
            className="inline-flex items-center gap-1.5 rounded-3xl text-sm text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            <IconArrowLeft className="size-4" aria-hidden />
            Back to the machine bills
          </Link>
        </>
      }
    >
      {missing ? (
        <Alert>
          <IconReceiptOff />
          <AlertTitle>There is no imported bill at this address</AlertTitle>
          <AlertDescription>
            This link may be out of date or mistyped. A sale rung up on a till is not a machine bill
            — look for it under Sales instead.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="pt-2">
          {state.kind === 'loading' && (
            <div aria-hidden className="h-64 animate-pulse rounded-3xl bg-muted" />
          )}

          {state.kind === 'failed' && (
            <div className="mx-auto max-w-md space-y-3 py-8 text-center">
              <LoadFailed
                error={state.error}
                onRetry={() => {
                  setReloads((count) => count + 1);
                }}
              />
            </div>
          )}

          {bill !== null && (
            <Record
              bill={bill}
              outletName={outlet?.name ?? null}
              items={items}
              onReverse={() => {
                setReversing(true);
              }}
            />
          )}
        </div>
      )}

      <ReverseMachineBillDialog
        bill={reversing ? bill : null}
        onClose={() => {
          setReversing(false);
        }}
        onReversed={(next) => {
          setReversing(false);
          // The answer is the bill as it now stands, reversal included.
          setState({ kind: 'loaded', value: next });
        }}
      />
    </Screen>
  );
}

function Record({
  bill,
  outletName,
  items,
  onReverse,
}: {
  bill: MachineBillResource;
  outletName: string | null;
  items: ReadonlyMap<string, CatalogueItemResource>;
  onReverse: () => void;
}) {
  const { reversal } = bill;
  const struck = reversal !== null ? 'text-muted-foreground line-through' : 'text-foreground';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
        <div className="space-y-2">
          <p
            className={`font-mono text-2xl numeric ${reversal !== null ? 'text-muted-foreground' : 'text-foreground'}`}
          >
            {bill.receiptNumber}
          </p>

          <div className="flex flex-wrap items-center gap-2">
            {reversal !== null && <Badge variant="outline">Reversed</Badge>}
            {bill.revision > 1 && (
              <Badge variant="secondary">Revision {String(bill.revision)}</Badge>
            )}
          </div>
        </div>

        <div className="text-right">
          <p className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
            Paid
          </p>
          <p className={`mt-1 font-mono text-2xl numeric ${struck}`}>
            {formatPaise(bill.totalPaise)}
          </p>
        </div>
      </div>

      {reversal !== null && (
        <div className="rounded-3xl border border-border bg-muted/40 p-4">
          <p className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
            Reversed
          </p>
          <p className="mt-2 text-sm text-foreground">
            Taken off the takings of{' '}
            <span className="numeric">{formatBusinessDate(bill.businessDate)}</span> on{' '}
            {formatInstant(reversal.recordedAt)}.
          </p>
          {reversal.reason !== null && (
            // Plain text: whatever was typed is rendered as typed.
            <p className="mt-2 text-sm text-muted-foreground">{reversal.reason}</p>
          )}
        </div>
      )}

      <Lines lines={bill.lines} items={items} totalPaise={bill.totalPaise} struck={struck} />

      <dl className="grid grid-cols-1 gap-x-8 gap-y-5 border-t border-border pt-6 sm:grid-cols-2">
        <Fact label="Machine">
          {bill.machineName}
          <span className="mt-0.5 block font-mono text-sm text-muted-foreground numeric">
            {bill.machineCode}
          </span>
        </Fact>

        <Fact label="Outlet">
          {outletName ?? <span className="font-mono text-xs numeric">{bill.outletId}</span>}
        </Fact>

        <Fact label="Trading day">
          <span className="numeric">{formatBusinessDate(bill.businessDate)}</span>
          <span className="mt-0.5 block text-sm text-muted-foreground">
            The bill&rsquo;s own date — the day its takings are counted in.
          </span>
        </Fact>

        <Fact label="Printed">
          {formatInstant(bill.occurredAt)}
          <span className="mt-0.5 block text-sm text-muted-foreground">
            The machine&rsquo;s own clock.
          </span>
        </Fact>

        <Fact label="Imported">
          {formatInstant(bill.recordedAt)}
          <span className="mt-0.5 block text-sm text-muted-foreground">
            {/* Header is row 1, so these match what a spreadsheet shows. */}
            {bill.fileRows.length === 1 ? 'Row ' : 'Rows '}
            <span className="font-mono numeric">{bill.fileRows.join(', ')}</span> of the file.{' '}
            <Link
              to={machineBillsForImportPath(bill.importId)}
              className="underline decoration-dotted underline-offset-4 hover:text-foreground"
            >
              Every bill from this upload
            </Link>
          </span>
        </Fact>

        <Fact label="Reference">
          <span className="font-mono text-xs numeric">{bill.saleId}</span>
        </Fact>
      </dl>

      {reversal === null && (
        <div className="flex justify-end border-t border-border pt-6">
          <Button
            variant="outline"
            aria-label={`Reverse bill ${bill.receiptNumber}`}
            onClick={onReverse}
          >
            Reverse this bill
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * The bill's lines. The total is the server's, never a sum of these rows.
 *
 * A machine may take a discount off a line (blossom-rest-api#510), and then the
 * total is the net — what was actually taken. Discount and Net appear only on a
 * bill that has one, so an undiscounted bill reads exactly as it always did.
 */
function Lines({
  lines,
  items,
  totalPaise,
  struck,
}: {
  lines: readonly MachineBillLine[];
  items: ReadonlyMap<string, CatalogueItemResource>;
  totalPaise: number;
  struck: string;
}) {
  const discounted = lines.some((line) => line.discountPaise > 0);

  return (
    <div className="overflow-hidden rounded-3xl border border-border">
      <Table>
        <caption className="sr-only">What the bill was for</caption>

        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead className="w-24 text-right">Quantity</TableHead>
            <TableHead className="w-36 text-right">Amount</TableHead>
            {discounted && (
              <>
                <TableHead className="w-36 text-right">Discount</TableHead>
                <TableHead className="w-36 text-right">Net</TableHead>
              </>
            )}
          </TableRow>
        </TableHeader>

        <TableBody>
          {lines.map((line, index) => {
            const item = items.get(line.catalogueItemId);

            return (
              // A line carries no id of its own; its position in the bill is stable.
              <TableRow key={index}>
                <TableCell className="whitespace-normal">
                  <div className="flex flex-wrap items-center gap-2">
                    {item === undefined ? (
                      <span className="font-mono text-xs numeric">{line.catalogueItemId}</span>
                    ) : (
                      <>
                        <span className="text-foreground">{item.name}</span>
                        <span className="font-mono text-sm text-muted-foreground numeric">
                          {item.code}
                        </span>
                      </>
                    )}
                    {line.itemRetiredAtSale && <Badge variant="secondary">Retired item</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono text-foreground numeric">
                  {line.quantity}
                </TableCell>
                <TableCell className="text-right font-mono text-foreground numeric">
                  {formatPaise(line.amountPaise)}
                </TableCell>
                {discounted && (
                  <>
                    <TableCell className="text-right font-mono text-foreground numeric">
                      {line.discountPaise > 0 ? formatPaise(line.discountPaise) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-foreground numeric">
                      {/* Integer paise; the API defines a line's take as exactly this. */}
                      {formatPaise(line.amountPaise - line.discountPaise)}
                    </TableCell>
                  </>
                )}
              </TableRow>
            );
          })}

          <TableRow className="hover:bg-transparent">
            <TableCell className="font-medium text-foreground">Total</TableCell>
            <TableCell />
            {discounted && (
              <>
                <TableCell />
                <TableCell />
              </>
            )}
            <TableCell className="text-right font-mono numeric">
              <span className={struck}>{formatPaise(totalPaise)}</span>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
        {label}
      </dt>
      <dd className="mt-1 text-foreground">{children}</dd>
    </div>
  );
}
